import {
    Box,
    Breadcrumbs,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import QuotaBlockedNotice from '../../components/subscription/QuotaBlockedNotice';
import DidNotLoad from '../../components/DidNotLoad';
import Loading from '../../components/Loading';
import { parseFormattedMoneyToCents } from '../../helpers/formatters';
import { filterActivePrinters, formatPrinterTypeLabel, parsePrinterTypeKey, printerTypeKey, uniquePrinterTypes } from '../../helpers/printers';

function ReplacementPartForm({ isEdit }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [printers, setPrinters] = useState([]);
    const [name, setName] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [printerType, setPrinterType] = useState('');
    const [unitValue, setUnitValue] = useState('');
    const [unitValueFloat, setUnitValueFloat] = useState(undefined);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [resultErrors, setResultErrors] = useState({ has_errors: false });

    useEffect(() => {
        api.get('printers?page=1&limit=100')
            .then((response) => {
                setPrinters(response.data.printers || []);
            })
            .catch(() => {
                if (!isEdit) {
                    setDidNotLoad(true);
                }
            });
    }, [isEdit]);

    useEffect(() => {
        if (!isEdit || !id) {
            return undefined;
        }
        setIsLoading(true);
        api.get(`replacement-parts/${id}`)
            .then((response) => {
                const data = response.data.data || {};
                setName(data.name || '');
                setQuantity(String(data.quantity ?? 0));
                setPrinterType(data.applies_to_all_printers || !data.printer_brand
                    ? ''
                    : printerTypeKey(data.printer_brand, data.printer_model));
                if (typeof data.unit_value_cents === 'number') {
                    const reais = data.unit_value_cents / 100;
                    setUnitValue(String(reais));
                    setUnitValueFloat(reais);
                }
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
        return undefined;
    }, [id, isEdit]);

    const printerTypes = useMemo(() => {
        const activeTypes = uniquePrinterTypes(filterActivePrinters(printers));
        if (!printerType) {
            return activeTypes;
        }
        if (activeTypes.some((type) => type.key === printerType)) {
            return activeTypes;
        }
        const { brand, model } = parsePrinterTypeKey(printerType);
        return [
            ...activeTypes,
            { key: printerType, brand, model, label: formatPrinterTypeLabel(brand, model) },
        ];
    }, [printers, printerType]);

    const renderFieldError = (field) => {
        if (!resultErrors[field]) {
            return null;
        }
        return (
            <div className="text-danger mt-1" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                {resultErrors[field]}
            </div>
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        const nextErrors = { has_errors: false };
        if (!name.trim()) {
            nextErrors.name = 'Informe o nome da peça.';
            nextErrors.has_errors = true;
        }
        const qty = parseInt(quantity, 10);
        if (Number.isNaN(qty) || qty < 0) {
            nextErrors.quantity = 'Informe uma quantidade válida.';
            nextErrors.has_errors = true;
        }
        if (nextErrors.has_errors) {
            setResultErrors(nextErrors);
            return;
        }
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        const { brand, model } = parsePrinterTypeKey(printerType);
        const payload = {
            name: name.trim(),
            quantity: qty,
            unit_value_cents: parseFormattedMoneyToCents(unitValueFloat, unitValue),
            printer_brand: brand,
            printer_model: model,
        };
        const request = isEdit
            ? api.put(`replacement-parts/${id}`, payload)
            : api.post('replacement-parts', payload);
        request
            .then(() => {
                navigate('/admin/replacement-parts');
            })
            .catch((error) => {
                try {
                    const data = error.response?.data || {};
                    setResultErrors({
                        ...(data.errors || {}),
                        has_errors: true,
                    });
                } catch {
                    setResultErrors({ has_errors: true });
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />;
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to="/admin">
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/replacement-parts')} to="/admin/replacement-parts">
                    Peças de reposição
                </Link>
                <Typography color="text.primary">
                    {isEdit ? 'Editando peça' : 'Adicionando peça'}
                </Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>
                {isEdit ? 'Editar peça de reposição' : 'Adicionar peça de reposição'}
            </Typography>
            {isEdit ? null : <QuotaBlockedNotice quotaKey="replacement_parts" backTo="/admin/replacement-parts" />}
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome da peça"
                            fullWidth
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                        {renderFieldError('name')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="part-printer-type-label">Impressora</InputLabel>
                            <Select
                                labelId="part-printer-type-label"
                                label="Impressora"
                                value={printerType}
                                onChange={(event) => setPrinterType(event.target.value)}
                            >
                                <MenuItem value="">Todas</MenuItem>
                                {printerTypes.map((type) => (
                                    <MenuItem key={type.key} value={type.key}>
                                        {type.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {renderFieldError('printer_brand')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Valor unitário"
                            fullWidth
                            customInput={TextField}
                            value={unitValue}
                            onValueChange={(values) => {
                                setUnitValue(values.value);
                                setUnitValueFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {renderFieldError('unit_value_cents')}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Quantidade"
                            fullWidth
                            type="number"
                            value={quantity}
                            onChange={(event) => setQuantity(event.target.value)}
                            inputProps={{ min: 0, step: 1 }}
                        />
                        {renderFieldError('quantity')}
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isSubmitting ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Box>
            </form>
        </div>
    );
}

export function RegisterReplacementPartPage() {
    return <ReplacementPartForm isEdit={false} />;
}

export function EditReplacementPartPage() {
    return <ReplacementPartForm isEdit />;
}
